import axios from 'axios';
import { StoreActionApi } from 'react-sweet-state';
import { enqueueSnackbar } from 'notistack';

import {
  createGetAction,
  createPutAction,
  createRestStore,
  errorNotificationSettings,
  Mapper,
  RestActionType,
  RestStoreState,
  setIsLoaded,
  setIsLoading,
} from '@intelliarts/ia-react-template';

import { Employee } from '../models/Employee';
import { EmployeeView } from '../models/EmployeeView';

export type EmployeeState = RestStoreState<Employee>;

const EMPTY_OBJECT = {};

export const employeesMapper: Mapper<EmployeeView, Employee> = {
  toEntity: model => {
    const { id, givenName, familyName, fullName, ...restProfile } = model;
    return ({
      ...restProfile,
      id: Number(model.id),
      name: { givenName, familyName, fullName },
    });
  },
  toModel: entity => {
    const { googleId, name, isGoogleUser, ...restProfile } = entity;
    return ({
      ...restProfile,
      givenName: name.givenName,
      familyName: name.familyName,
      fullName: `${name.familyName} ${name.givenName}`,
    });
  },
};

const actionConfig = {
  resourcePath: '/employees',
};

// Sync is served by the user-service, reached through the `/user/` gateway prefix
// (rewritten to `/api/v1/` by the proxy), mirroring the `/benefit/` prefix.
const USER_SERVICE_BASE_URL = '/user/api/v1';
const userServiceAxiosInstance = axios.create({ baseURL: USER_SERVICE_BASE_URL });

export const employeeActions = {
  [RestActionType.GET]: createGetAction<Employee>({ resourcePath: `${actionConfig.resourcePath}?withArchived=true` }),
  [RestActionType.PUT]: createPutAction<Employee, EmployeeView>({ resourcePath: actionConfig.resourcePath}, employeesMapper),
  loadActiveEmployees: createGetAction<Employee>({ resourcePath: actionConfig.resourcePath }),
  syncEmployeesOnDependentServices: () => async ({ dispatch }: StoreActionApi<EmployeeState>) => {
    dispatch(setIsLoading(EMPTY_OBJECT));
    try {
      await userServiceAxiosInstance.put('/users/sync', {}, { timeout: 120000 }); // timeout 120000 = 2 minute
    } catch (e: any) {
      console.error('Sync employees on dependant services command failed.', e);
      enqueueSnackbar(e.message, errorNotificationSettings.settings);
    }
    dispatch(setIsLoaded(EMPTY_OBJECT));
  }
};

export const EmployeesStore = createRestStore<EmployeeView, Employee>({
  actions: employeeActions,
  actionConfig,
  containerDisplayName: 'employees-container',
  mapper: employeesMapper,
});
